import React from 'react';
import type { FC } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import SectionMessage from '@atlaskit/section-message/message';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

// oxlint-disable-next-line typescript/consistent-type-imports
import {
	ANALYTICS_CREATE_SOURCE,
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
	MODAL_TYPE,
	type AnalyticsOrigin,
} from '../constants/snippetAnalytics.ts';
import { useAiSuggestions } from '../hooks/modals/useAiSuggestions.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import { useSnippetModalClose } from '../hooks/modals/useSnippetModalClose.ts';
import { useOpenSnippetPanel } from '../hooks/panel/useOpenSnippetPanel.ts';
import { isBodyTooLargeError } from '../services/types/blockServiceTypes.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';
import { wrapAdfWithDocNode, adfContainsNodeType } from '../utils/adfUtils.ts';

import { getSnippetBaseModalProps, SnippetBaseModal } from './base/SnippetBaseModal.tsx';
import { messages as baseMessages } from './base/SnippetBaseModal.i18n.ts';
import type { SnippetBaseModalFormValues } from './utils/snippetModalFormUtils.ts';
import { getDefaultFormValues } from './utils/snippetModalFormUtils.ts';
import { FirstSnippetCreatedModal } from './onboarding/FirstSnippetCreatedModal.tsx';
import { createSnippetModalMessages } from './CreateSnippetModal.i18n.ts';

export type CreateSnippetModalProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	adf?: ADFEntity | ADFEntity[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	analyticsOrigin: AnalyticsOrigin;
	createSource?: ANALYTICS_CREATE_SOURCE;
} & WithFlagsProps;

const FORM_KEY = 'create-snippet';

const CreateSnippetModalWithFlags: FC<CreateSnippetModalProps> = ({
	onClose,
	onCloseComplete,
	adf,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
	analyticsOrigin,
	createSource,
}) => {
	const intl = useIntl();
	const openSnippetPanel = useOpenSnippetPanel();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { showModal } = useDialogs();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
	);
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const handleCloseBase = useSnippetModalClose(
		onClose,
		ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
		ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET,
	);
	const handleClose = (attrs: Record<string, unknown>) => {
		handleCloseBase({ ...attrs, createSource });
	};
	const { isAiSuggestionLoading, aiSuggestedName, aiSuggestedDescription } = useAiSuggestions(
		adf,
		ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
	);

	const {
		data: onboardingData,
		loading: onboardingLoading,
		error: onboardingError,
	} = useGetOnboardingState([SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL], false);
	const { hasSeenFirstSnippetCreatedModal } = deserializeState(onboardingData);

	const baseDefaultValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const defaultValues: SnippetBaseModalFormValues = {
		...baseDefaultValues,
		snippetName: aiSuggestedName ?? baseDefaultValues.snippetName,
		snippetDescription: aiSuggestedDescription ?? baseDefaultValues.snippetDescription,
	};
	const initialAdf = wrapAdfWithDocNode(adf);
	const isSyncBlockSource = adfContainsNodeType(initialAdf, 'bodiedSyncBlock');
	const title = <FormattedMessage {...createSnippetModalMessages.titleCreate} />;
	const submitLabel = <FormattedMessage {...createSnippetModalMessages.createButton} />;

	const successFlagActions =
		analyticsOrigin === ANALYTICS_ORIGIN.SNIPPET_GALLERY
			? undefined
			: [
					{
						content: <FormattedMessage {...baseMessages.successFlagViewTemplateAction} />,
						onClick: openSnippetPanel,
					},
				];

	const onSubmit = async (values: SnippetBaseModalFormValues, currentAdf: DocNode) => {
		const bodySize = JSON.stringify(currentAdf).length;
		const isOnboardingStateReady = !onboardingLoading && !onboardingError;
		const shouldShowFirstSnippetCreatedModal =
			!isEmbeddedConfluence && isOnboardingStateReady && !hasSeenFirstSnippetCreatedModal;

		const newSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared,
				successFlagActions,
				showSuccessFlag: !shouldShowFirstSnippetCreatedModal,
				onShareFailure: (snippetId: string) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SHARE, { snippetId }),
				onSaveFailure: (error: unknown) => {
					const isBodyTooLarge = isBodyTooLargeError(error);
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_CREATE, {
						...(isBodyTooLarge && { isBodyTooLarge: true, bodySize }),
					});
				},
				messages: {
					successFlagTitle: createSnippetModalMessages.successFlagTitle,
					errorFlagTitle: createSnippetModalMessages.errorFlagTitle,
					errorFlagDescription: createSnippetModalMessages.errorFlagDescription,
				},
			},
		);
		if (newSnippet) {
			const wasNameSuggestedByAi = aiSuggestedName !== null;
			const wasDescriptionSuggestedByAi = aiSuggestedDescription !== null;
			fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SAVE_SUCCESS, {
				snippetId: newSnippet.snippetId,
				bodySize,
				modalType: MODAL_TYPE.CREATE,
				templateType: newSnippet.templateType,
				createSource,
			});
			handleClose({
				outcome: MODAL_OUTCOME.CREATED,
				snippetId: newSnippet.snippetId,
				bodySize,
				templateType: newSnippet.templateType,
				wasNameSuggestedByAi,
				...(wasNameSuggestedByAi && {
					nameKeptAsSuggested: values.snippetName === aiSuggestedName,
				}),
				wasDescriptionSuggestedByAi,
				...(wasDescriptionSuggestedByAi && {
					descriptionKeptAsSuggested: values.snippetDescription === aiSuggestedDescription,
				}),
				createSource,
			});

			if (shouldShowFirstSnippetCreatedModal) {
				showModal(FirstSnippetCreatedModal, {});
			}
		}
	};

	const formWarning = isSyncBlockSource ? (
		<SectionMessage appearance="warning">
			<FormattedMessage {...createSnippetModalMessages.syncBlockSourceWarning} />
		</SectionMessage>
	) : undefined;

	return (
		<SnippetBaseModal
			{...getSnippetBaseModalProps({
				title,
				submitLabel,
				formKey: FORM_KEY,
				defaultValues,
				initialAdf,
				onSubmit,
				handleClose,
				fireFailedAnalytic,
				onCloseComplete,
				contentId,
				spaceId,
				spaceKey,
				isSnippetSharingForSiteEnabled,
				isAiSuggestionLoading,
				formWarning,
			})}
		/>
	);
};

export const CreateSnippetModal: React.ComponentType<
	Omit<
		{
			onClose: () => void;
			onCloseComplete: () => void;
			adf?: ADFEntity | ADFEntity[];
			contentId: string;
			spaceId: string;
			spaceKey: string;
			editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
			analyticsOrigin: AnalyticsOrigin;
			createSource?: ANALYTICS_CREATE_SOURCE;
		},
		'flags'
	>
> = withFlags(CreateSnippetModalWithFlags);
